import React, { useEffect, useRef, useState } from 'react';
import { BackBar, BackButton } from '../../../layout/back-button';
import { NewBuildingData } from '../../../../interfaces/building-interfaces';
import { colors } from '../../../../style-utils/colors';
import { Text } from '../../../../style-utils/text';
import { RightArrow } from '../../../icons/right-arrow';
import {
  AddNewPropertyForm,
  AddNewPropertyFormContainer,
  SaveButton,
  ButtonTitle,
  DashboardContent,
  InputContainer,
  Label,
  StyledBuildingInput,
  StyledBuildingSelect,
  Title,
  ButtonsContainer,
  CancelButton,
  FieldError,
  FieldHint,
  FormBanner,
  FormFieldset,
} from './dashboard-buildings.styled';
import { InlineMessage, Staleable, StatusLabel } from '../../../../motion';
import { useUnitAllowance } from '../../../hooks/use-unit-allowance';
import { BuyUnitsDialog } from '../../../units/buy-units-dialog';
import { UnitLimitError, asUnitError } from '../../../../api/units-client';
import { BuildingPortfolioField } from './building-portfolio-field';
import {
  BuildingLocationPicker,
  BuildingLocation,
  EMPTY_LOCATION,
} from './building-location-picker';
import { BuildingTimeZoneField, useBuildingZone } from './building-time-zone';
import { regexValidation } from '../../../../function-utils/regex-validation';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiRoute } from '../../../../constants/api-constants';
import toast from '../../../../utils/toast';
import {
  focusFirstInvalid,
  focusField,
  serverFieldError,
} from '../../../../utils/field-errors';

interface StaffingOption {
  code: string;
  label: string;
}

const newBuildingInitialState: NewBuildingData = {
  property_name: '',
  country: 'USA',
  address: '',
  city: '',
  staffing_code: '',
  portfolio_id: '',
  new_portfolio_name: '',
  email: '',
  phone_number: '',
};

export const DashboardAddNewBuilding = () => {
  const [newBuildingData, setNewBuildingData] = useState<NewBuildingData>(
    newBuildingInitialState
  );
  // The building's own clock. It follows the map pin once there is one; until
  // then it shows the browser's zone, and typing in the field is an explicit
  // choice that wins over both (see building-time-zone.tsx).
  const zoneState = useBuildingZone({ savedZone: '' });
  // The picked place. Held apart from newBuildingData because it is written as
  // a whole object by the picker rather than field-by-field by handleInputChange.
  const [location, setLocation] = useState<BuildingLocation>({
    ...EMPTY_LOCATION,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submitRef = useRef(false);
  const [staffingOptions, setStaffingOptions] = useState<StaffingOption[]>([]);
  const [invalidFieldsInputArray, setInvalidFieldsInputArray] = useState<
    string[]
  >([]);
  // The org's prepaid pool, so this form can say "23 units, you have 4" BEFORE
  // the server has to refuse — and open the upsell instead of a dead end.
  const allowance = useUnitAllowance();
  const [upsellOpen, setUpsellOpen] = useState(false);
  // A toast is easy to miss and disappears; a save that failed needs to stay on
  // screen next to the button that failed. Both are used — this is the one that
  // persists until something changes.
  const [formError, setFormError] = useState('');
  // Typed an address, never picked a match. The picker owns this fact
  // because the typed text is its local state; the form only ever sees an
  // empty location object and cannot tell it apart from 'left blank'.
  const [locationUnresolved, setLocationUnresolved] = useState(false);

  const requestedUnits = Number(newBuildingData.units ?? 0);
  const overUnitCap =
    Number.isFinite(requestedUnits) &&
    requestedUnits > 0 &&
    requestedUnits > allowance.remaining;

  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${apiRoute}/staffing-models`)
      .then(response => {
        setStaffingOptions(response.data || []);
      })
      .catch(() => {
        // staffing is optional; the select still renders a prompt
      });
  }, []);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const validateNewBuildingData = (data: any) => {
    // Name and unit count only. country / address / city / phone_number are all
    // nullable in the database (migration 024) and the signup wizard creates
    // buildings without them, so requiring them here made this form stricter
    // than the product — and made the edit form unable to save those buildings
    // at all. Contact details stay optional; format is still checked below when
    // something IS entered.
    const requiredFields: string[] = ['property_name'];

    const emptyFields: string[] = [];

    requiredFields.forEach(field => {
      if (!data[field] || data[field].length === 0) {
        emptyFields.push(field);
      }
    });

    // Units are OPTIONAL here now. A building is a group of units, and units are
    // what the account is billed for — so allocating them is its own step, drawn
    // from the org's prepaid pool, not a number typed on the way past. Requiring
    // it here forced every new building to spend capacity at the moment of
    // creation, which is what left accounts at zero remaining. A number that IS
    // typed still has to be a positive whole one.
    if (data.units !== undefined && data.units !== '' && data.units < 1) {
      emptyFields.push('units');
    }

    // AN ADDRESS THAT WAS TYPED AND NOT PICKED IS AN ERROR, an address that was
    // never typed is not. Location stays optional; silently discarding one that
    // the user can still see in the box does not.
    if (locationUnresolved) {
      emptyFields.push('location');
    }

    // Only validate the property email if one was actually typed.
    if (newBuildingData.email && newBuildingData.email.length > 0) {
      const emailValidation = regexValidation('email', newBuildingData.email);

      if (!emailValidation.isValidEmail) {
        emptyFields.push('email');
      }
    }

    return emptyFields;
  };

  const handleInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    if (name === 'phone_number') {
      // Remove non-numeric characters
      let numericValue = value.replace(/\D/g, '');
      numericValue = '+' + numericValue;

      setNewBuildingData({ ...newBuildingData, [name]: numericValue });
    } else if (name === 'units') {
      const numericValue = value.replace(/\D/g, '');

      setNewBuildingData({
        ...newBuildingData,
        units: numericValue === '' ? undefined : Number(numericValue),
      });
    } else {
      setNewBuildingData({ ...newBuildingData, [name]: value });
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    if (submitRef.current) {
      return;
    }

    const invalidFields = validateNewBuildingData(newBuildingData);

    setInvalidFieldsInputArray(invalidFields);
    setFormError('');

    if (invalidFields.length > 0) {
      // A silent `return` was the whole problem: the inline labels sit next to
      // their fields, which can be off-screen on a long form, so a refused save
      // looked like a dead button. Say it once, at the top, in the same place
      // every failure is reported.
      setFormError(
        invalidFields.length === 1
          ? 'One field needs attention before this can be saved.'
          : `${invalidFields.length} fields need attention before this can be saved.`
      );
      /* AND GO THERE — the banner says which count, not which field, and on a
         long form the field it means is off-screen. First one, top to bottom. */
      focusFirstInvalid(invalidFields);

      return;
    }

    // Out of units, caught before the request. The server enforces this too —
    // this is the version that offers a way forward instead of an error.
    if (overUnitCap) {
      setUpsellOpen(true);

      return;
    }

    submitRef.current = true;
    setIsLoading(true);
    try {
      const formData = new FormData();

      formData.append('property_name', newBuildingData.property_name);
      formData.append('units', String(newBuildingData.units ?? ''));
      formData.append('staffing_code', newBuildingData.staffing_code ?? '');
      // Both are sent empty when the user chose nothing; the server treats an
      // empty value as "the organization's default portfolio", which is exactly
      // what happened before a portfolio could be chosen at all.
      formData.append('portfolio_id', newBuildingData.portfolio_id ?? '');
      formData.append(
        'new_portfolio_name',
        newBuildingData.new_portfolio_name ?? ''
      );
      // The picked place. city / address / country come from here now — the
      // separate inputs are gone, but the columns are still written because the
      // reports and the roster read them.
      // `?? ''` throughout: FormData turns a JS null into the string "null".
      Object.entries(location).forEach(([field, fieldValue]) => {
        formData.append(field, fieldValue === null ? '' : String(fieldValue));
      });
      // One optional property email. The nine extra address fields this form
      // used to carry are gone — properties.email is still an array column, so
      // nothing about the data moved, but the form no longer offers to fill ten
      // slots nobody reads.
      formData.append('email', newBuildingData.email);
      formData.append('phone_number', newBuildingData.phone_number ?? '');
      // ---- A BUILDING ALWAYS LEAVES HERE WITH A ZONE TO FALL BACK ON ----
      //
      // The server decides the zone: from the pin when there is one, from an
      // explicit choice when the field was typed into, and otherwise from the
      // browser's zone sent here. "No opinion" is not a thing a building can
      // have about where it is — an omitted field used to mean the 'UTC' column
      // default, which is how a desk in Sofia came to read 07:24 for an event
      // it watched at 09:24 (owner report, 2026-08-13).
      Object.entries(zoneState.fields()).forEach(([field, fieldValue]) => {
        formData.append(field, fieldValue);
      });

      await axios.post(
        `${apiRoute}/dashboard/buildings/add-new-property`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      toast.success('Building added');
      setNewBuildingData(newBuildingInitialState);
      setInvalidFieldsInputArray([]);
      navigate('/dashboard/buildings');
    } catch (caught) {
      const error = asUnitError(caught);

      // Out of units. Not an error the user can retype their way out of, so
      // offer the purchase rather than a red line telling them they are stuck.
      if (error instanceof UnitLimitError) {
        setFormError(error.message);
        setUpsellOpen(true);
        await allowance.reload();

        return;
      }

      /* WHEN THE SERVER NAMES A FIELD, POINT AT IT. A rejected time zone comes
         back as { message, field: 'timezone' } — the field name was being
         discarded and the reason raised as a toast, detached from the input
         that caused it. Mark, say, and move; toast only when the answer names
         nothing to move to. */
      const named = serverFieldError(error);
      if (named) {
        setInvalidFieldsInputArray(prev =>
          prev.includes(named.field) ? prev : [...prev, named.field]
        );
        setFormError(named.message);
        /* AFTER THE FORM IS LIVE AGAIN. The fields sit in a fieldset that is
           disabled for the whole of the request, and a disabled input refuses
           focus — so the move waits for the render that re-enables them. */
        setTimeout(() => focusField(named.field), 0);

        return;
      }

      const message = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;
      const text =
        message ||
        (axios.isAxiosError(error) && !error.response
          ? 'We could not reach the server. Check your connection and try again.'
          : 'Could not add the building. Please try again.');

      // Both: the toast catches the eye, the banner is still there when they
      // look back at the form.
      setFormError(text);
      toast.error(text);
    } finally {
      submitRef.current = false;
      setIsLoading(false);
    }
  };

  const clearData = () => {
    setNewBuildingData(newBuildingInitialState);
    setInvalidFieldsInputArray([]);
    navigate('/dashboard/buildings');
  };

  return (
    <DashboardContent
      id="dashboard-add-new-building-content"
      isAddNewBuilding={true}
    >
      {/* A ROUTED PAGE NEEDS A WAY OUT (WP-10 / R-9). This is a route,
          not a modal — there is no scrim to click and no close button —
          so without this the only exit was the browser's own back.
          Modals deliberately do NOT get one: a BackBar beside a close
          affordance is the second dismiss control R-9 forbids. */}
      <BackBar>
        <BackButton to="/dashboard/buildings" label="Back to buildings" />
      </BackBar>
      <Title>
        <Text
          mt={16}
          ml={16}
          mb={0}
          fontSize={24}
          fontWeight={700}
          color={colors.grey_dark}
        >
          Add new property
        </Text>
        <Text mt={8} ml={16} mb={0} fontSize={14} color={`${colors.grey}`}>
          Please fill in the form
        </Text>
      </Title>

      <AddNewPropertyFormContainer>
        {/* SAVING KEEPS THE FORM ON SCREEN. The absolute spinner that used to
            cover these fields is gone: while the POST is in flight the form
            stays readable, dims (Staleable), is inert (the fieldset is
            disabled, aria-busy says so) and the Save button carries the
            pending state at a stable width. */}
        <AddNewPropertyForm
          id="dashboard-add-new-building-form"
          aria-busy={isLoading || undefined}
          onSubmit={onSubmit}
        >
          {/* role="alert" so this is announced, not just drawn. Sits at the top
              of the form because that is where the eye returns after a save that
              did not happen. Unfolds rather than pops, so the fields slide. */}
          <InlineMessage
            role="alert"
            message={
              formError ? <FormBanner as="div">{formError}</FormBanner> : undefined
            }
          />

          <Staleable stale={isLoading}>
          <FormFieldset disabled={isLoading}>

          {/* The single "Upload photo" control that used to sit here is gone.
              Buildings have a real gallery now (EntityGallery, backend
              migration 075) and the server ignores `property_image` entirely —
              so this control looked like it worked, silently dropped the file,
              and left the user believing they had set a photo.

              A gallery cannot be offered on the CREATE form: images hang off a
              building id that does not exist until the building is saved. The
              gallery lives on the edit modal, one step later. */}

          <InputContainer>
            <Label htmlFor="new-building-name">Building name</Label>
            <StyledBuildingInput
              id="new-building-name"
              type="text"
              name="property_name"
              value={newBuildingData.property_name}
              placeholder={'Enter building name'}
              onChange={handleInputChange}
            />
          </InputContainer>
          <FieldError show={invalidFieldsInputArray.includes('property_name')}>
            Please enter building name*
          </FieldError>

          <InputContainer>
            <Label htmlFor="new-building-units">
              Number of units (optional)
            </Label>
            <StyledBuildingInput
              id="new-building-units"
              type="number"
              min={1}
              name="units"
              value={newBuildingData.units ?? ''}
              placeholder={'Leave blank and allocate later'}
              onChange={handleInputChange}
            />
            <FieldHint>
              {allowance.capped
                ? `Units come out of your plan — ${allowance.remaining} of ${
                    allowance.allowance?.units_allowed ?? 0
                  } available. You can leave this blank and allocate units from the Units screen whenever you're ready.`
                : 'You can leave this blank and add units from the Units screen whenever you’re ready.'}
            </FieldHint>
          </InputContainer>
          <FieldError show={invalidFieldsInputArray.includes('units')}>
            Enter a whole number greater than zero, or leave it blank*
          </FieldError>
          <FieldError show={overUnitCap}>
            That is more units than you have available
            {allowance.canBuy ? ' — add more to continue*' : '*'}
          </FieldError>

          {/* Replaces the separate City / Country / Address inputs: picking a
              place on the map fills all three behind the scenes. */}
          <BuildingLocationPicker
            value={location}
            onChange={setLocation}
            onUnresolvedChange={setLocationUnresolved}
            onZoneHint={zoneState.onZoneHint}
            invalid={invalidFieldsInputArray.includes('location')}
          />

          {/* THE BUILDING'S TIME ZONE. It governs everything the desk reads:
              which day an entry belongs to, when a shift starts and ends, the
              window a handover covers, and the clock beside every row in the
              activity feed. A building on the wrong zone shows the right events
              at the wrong times, which is the hardest kind of wrong to notice —
              so it is taken from where the building IS, not from where the
              person adding it happens to be sitting. */}
          <BuildingTimeZoneField idPrefix="new-building" state={zoneState} />

          <InputContainer>
            <Label htmlFor="new-building-staffing">Front desk staffing</Label>
            <StyledBuildingSelect
              id="new-building-staffing"
              name="staffing_code"
              value={newBuildingData.staffing_code ?? ''}
              onChange={handleInputChange}
            >
              <option value="">Select…</option>
              {staffingOptions.map(option => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </StyledBuildingSelect>
          </InputContainer>

          <BuildingPortfolioField
            mode="create"
            portfolioId={newBuildingData.portfolio_id ?? ''}
            newPortfolioName={newBuildingData.new_portfolio_name ?? ''}
            onChange={next =>
              setNewBuildingData(current => ({
                ...current,
                portfolio_id: next.portfolioId,
                new_portfolio_name: next.newPortfolioName,
              }))
            }
          />

          {/* One optional contact address for the building. This used to be ten
              fields ("second email" … "tenth email") behind show/hide toggles;
              nothing in the app ever read past the first, so the rest were a
              deprecated data-entry burden. */}
          <InputContainer>
            <Label htmlFor="new-building-email">
              Property email (optional)
            </Label>
            <StyledBuildingInput
              id="new-building-email"
              type="email"
              name="email"
              value={newBuildingData.email}
              placeholder={'Enter building email address'}
              onChange={handleInputChange}
            />
          </InputContainer>
          <FieldError show={invalidFieldsInputArray.includes('email')}>
            Please enter a valid email address*
          </FieldError>

          {/* One plain phone box. The country-code <select> that stood beside it
              is gone, along with the '+1' this input displayed whenever
              phone_number was empty — a box that LOOKED filled while the state
              behind it was blank, on a field the server has never required. On
              the edit twin the same control destroyed saved numbers outright
              (its handler blanked phone_number before writing the prefix), which
              is why both forms lose it. The wire field was always this one
              string; the select only ever decorated it. */}
          <InputContainer>
            <Label htmlFor="new-building-phone">Phone (optional)</Label>
            <StyledBuildingInput
              id="new-building-phone"
              type="tel"
              name="phone_number"
              autoComplete="tel"
              value={newBuildingData.phone_number}
              placeholder={'Enter phone number'}
              onChange={handleInputChange}
            />
          </InputContainer>
          <FieldError show={invalidFieldsInputArray.includes('phone_number')}>
            Please enter phone number*
          </FieldError>
          </FormFieldset>
          </Staleable>
        </AddNewPropertyForm>
      </AddNewPropertyFormContainer>
      <ButtonsContainer>
        <CancelButton type="button" onClick={() => clearData()}>
          <ButtonTitle>Cancel</ButtonTitle>
        </CancelButton>
        <SaveButton
          type="submit"
          form="dashboard-add-new-building-form"
          disabled={isLoading}
        >
          <StatusLabel
            state={isLoading ? 'busy' : 'idle'}
            labels={{
              idle: (
                <>
                  <ButtonTitle>Save</ButtonTitle>
                  <RightArrow />
                </>
              ),
              busy: <ButtonTitle>Saving…</ButtonTitle>,
            }}
          />
        </SaveButton>
      </ButtonsContainer>

      <BuyUnitsDialog
        open={upsellOpen}
        reason={`Adding ${requestedUnits} units to ${
          newBuildingData.property_name || 'this building'
        }`}
        unitsAllowed={allowance.allowance?.units_allowed ?? null}
        unitsUsed={allowance.allowance?.units_used ?? 0}
        shortfall={
          Number.isFinite(allowance.remaining)
            ? Math.max(1, requestedUnits - allowance.remaining)
            : 1
        }
        canBuy={allowance.canBuy}
        comped={allowance.comped}
        onDismiss={() => setUpsellOpen(false)}
        onBought={async () => {
          setUpsellOpen(false);
          setFormError('');
          await allowance.reload();
          toast.success('Units added — press Save to add the building.');
        }}
      />
    </DashboardContent>
  );
};
